import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, FinalCTA, Breadcrumbs } from "@/components/site/Layout";
import { locations } from "@/data/locations";
import { PricingTeaser } from "@/components/site/Pricing";
import { CaseStudy, locationCaseStudy } from "@/components/site/CaseStudy";
import { FAQSection } from "@/components/site/FAQSection";
import { breadcrumbSchema, jsonLdScript, organizationSchema } from "@/lib/seo";

export const Route = createFileRoute("/lawyer-seo/")({
  head: () => ({
    meta: [
      { title: "Lawyer SEO by Location | City & State SEO for Law Firms" },
      { name: "description", content: "Local SEO for law firms in 60+ cities and states across the US. Specialist lawyer SEO services tied to your geography and competition." },
      { property: "og:title", content: "Lawyer SEO by Location | PageOne Lawyers" },
      { property: "og:description", content: "Local SEO for law firms in 60+ US markets." },
      { property: "og:url", content: "https://pageonelawyers.com/lawyer-seo" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://pageonelawyers.com/lawyer-seo" }],
    scripts: [
      jsonLdScript(organizationSchema()),
      jsonLdScript(breadcrumbSchema([
        { label: "Home", path: "/" },
        { label: "Locations", path: "/lawyer-seo" },
      ])),
    ],
  }),
  component: LocationsIndex,
});

function LocationsIndex() {
  const sorted = [...locations].sort((a, b) => a.location.localeCompare(b.location));
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Locations" }]} />
          <Eyebrow><span className="text-[var(--gold)]">Locations</span></Eyebrow>
          <h1 className="mt-6 font-display text-5xl md:text-6xl font-semibold max-w-3xl text-primary-foreground">Lawyer SEO services, mapped to your market.</h1>
          <p className="mt-6 max-w-2xl text-lg text-primary-foreground/80">Every city has a different local pack, different competitors, and different intent. Choose your market, we'll show you what it takes to rank there.</p>
        </div>
      </section>
      <Section>
        <Eyebrow>Cities & states we rank</Eyebrow>
        <h2 className="mt-4 text-4xl font-semibold text-foreground max-w-3xl">{sorted.length} markets, and growing.</h2>
        <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {sorted.map((l) => (
            <Link key={l.slug} to="/lawyer-seo/$slug" params={{ slug: l.slug }} className="group rounded-lg border border-border bg-card p-5 hover:border-[var(--gold)] hover:shadow-[var(--shadow-card)] transition">
              <div className="text-lg font-semibold text-foreground">Lawyer SEO {l.location}</div>
              <div className="mt-3 text-sm font-semibold text-primary group-hover:text-[var(--gold-deep)]">View market →</div>
            </Link>
          ))}
        </div>
      </Section>
      <PricingTeaser />
      <CaseStudy {...locationCaseStudy("Austin")} />
      <FAQSection
        tone="muted"
        title="Location SEO for law firms, common questions."
        items={[
          { q: "Do you rank firms in every US city?", a: "We work in 60+ major US markets. If you don't see your city listed, ask, we may already be building it or take on new markets each quarter." },
          { q: "How does local SEO differ from national SEO for lawyers?", a: "Local SEO targets the Google map pack, GBP visibility, and city-specific search intent. National SEO targets broader informational and brand queries. Most law firms need both, weighted toward local." },
          { q: "Can you rank a firm in multiple cities?", a: "Yes, multi-location firms are one of our specialties. We build per-city landing pages, per-office GBPs, and per-market link programs." },
          { q: "What is the minimum engagement to see local pack results?", a: "90–120 days for early movement, 6–9 months for durable top-3 local pack rankings in competitive metros." },
          { q: "Do you guarantee exclusivity in a market?", a: "Yes, one firm per practice area per market. If we take your city and practice, we won't take a competitor." },
        ]}
      />
      <FinalCTA />
    </Layout>
  );
}